import React from 'react';
import { Plus, X } from 'lucide-react';
import { AutoResizeTextarea } from '../../../components/AutoResizeTextarea';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS, APP_TEXTAREA_CLASS } from '../../../ui/controlStyles';
import type { Task, TaskLeadOption, TaskMember } from '../types';
import type { TasksApi } from '../useTasks';
import { PanelIconButton, TASK_TITLE_INPUT_CLASS, TaskFields, TaskPanelFrame, type TaskFieldValues } from './TaskFields';
import { TASK_SECTION_LABEL_CLASS } from './taskUi';

type Props = {
  defaults: Pick<TaskFieldValues, 'due_date' | 'assigned_user_id'>;
  members: TaskMember[];
  leadOptions: TaskLeadOption[];
  instancePrefix: string;
  canOpenProjects: boolean;
  overlay: boolean;
  tasksApi: TasksApi;
  onCreated: (task: Task) => void;
  /** Meldet, ob schon etwas eingegeben wurde (Schutz vor stillem Verwerfen). */
  onDirtyChange: (dirty: boolean) => void;
  onClose: () => void;
};

/** Neue Aufgabe im rechten Panel — gleiche Felder wie die Detailansicht, gespeichert erst mit „Anlegen". */
export function TaskCreatePanel({
  defaults,
  members,
  leadOptions,
  instancePrefix,
  canOpenProjects,
  overlay,
  tasksApi,
  onCreated,
  onDirtyChange,
  onClose,
}: Props) {
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [fields, setFields] = React.useState<TaskFieldValues>({
    ...defaults,
    priority: 'mittel',
    lead: null,
    recurrence: null,
  });
  const [checklist, setChecklist] = React.useState<string[]>([]);
  const [newCheck, setNewCheck] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const titleRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const cleanTitle = title.replace(/\s+/g, ' ').trim();
  const dirty = Boolean(cleanTitle || description.trim() || checklist.length || newCheck.trim());
  React.useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const submit = async () => {
    if (!cleanTitle || saving) return;
    setSaving(true);
    const pending = newCheck.trim();
    const items = pending ? [...checklist, pending] : checklist;
    const created = await tasksApi.create({
      title: cleanTitle,
      description: description.trim() ? description : undefined,
      due_date: fields.due_date,
      priority: fields.priority,
      assigned_user_id: fields.assigned_user_id,
      lead_id: fields.lead?.id ?? null,
      recurrence: fields.recurrence,
    });
    if (!created) {
      setSaving(false);
      return;
    }
    // Reihenfolge erhalten: Punkte nacheinander anlegen.
    for (const text of items) await tasksApi.addChecklistItem(created.id, text);
    onCreated(created);
  };

  const addCheck = () => {
    const t = newCheck.trim();
    if (!t) return;
    setChecklist((list) => [...list, t]);
    setNewCheck('');
  };

  return (
    <TaskPanelFrame
      overlay={overlay}
      onBack={onClose}
      label="Neues To-do"
      header={
        <>
          <span className="text-xs font-medium text-app-text-subtle">Neues To-do</span>
          <PanelIconButton title="Abbrechen (Esc)" onClick={onClose} className="max-md:hidden">
            <X className="h-4 w-4" strokeWidth={1.8} />
          </PanelIconButton>
        </>
      }
      footer={
        <div className="flex shrink-0 justify-end gap-2 px-5 py-3">
          <button type="button" onClick={onClose} className={`${APP_BTN_OUTLINE_CLASS} !h-8`}>
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!cleanTitle || saving}
            className={`${APP_BTN_PRIMARY_CLASS} !h-8`}
          >
            {saving ? 'Wird angelegt…' : 'To-do anlegen'}
          </button>
        </div>
      }
    >
      <AutoResizeTextarea
        ref={titleRef}
        value={title}
        rows={1}
        aria-label="Titel"
        placeholder="Was ist zu tun?"
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void submit();
          }
        }}
        className={TASK_TITLE_INPUT_CLASS}
      />

      <TaskFields
        values={fields}
        onChange={(p, lead) =>
          setFields((f) => ({
            ...f,
            ...('due_date' in p ? { due_date: p.due_date ?? null } : {}),
            ...('assigned_user_id' in p ? { assigned_user_id: p.assigned_user_id ?? null } : {}),
            ...(p.priority ? { priority: p.priority } : {}),
            ...('recurrence' in p ? { recurrence: p.recurrence ?? null } : {}),
            ...(lead !== undefined ? { lead } : {}),
          }))
        }
        members={members}
        leadOptions={leadOptions}
        canEdit
        canOpenProjects={canOpenProjects}
        instancePrefix={instancePrefix}
      />

      <div className="flex flex-col gap-2">
        <span className={TASK_SECTION_LABEL_CLASS}>Beschreibung</span>
        <AutoResizeTextarea
          value={description}
          rows={3}
          maxHeight={400}
          aria-label="Beschreibung"
          placeholder="Hier Beschreibung eingeben…"
          onChange={(e) => setDescription(e.target.value)}
          className={`${APP_TEXTAREA_CLASS} resize-none leading-relaxed`}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className={`pb-1 ${TASK_SECTION_LABEL_CLASS}`}>Checkliste</span>
        {checklist.map((text, i) => (
          <div
            key={`${i}-${text}`}
            className="group -mx-1.5 flex min-h-8 items-center gap-2.5 rounded-md px-1.5 text-sm hover:bg-app-hover"
          >
            <input type="checkbox" disabled className="h-4 w-4 shrink-0 accent-pipedrive-blue" aria-hidden />
            <span className="min-w-0 flex-1 py-1 text-app-text-muted">{text}</span>
            <button
              type="button"
              onClick={() => setChecklist((list) => list.filter((_, j) => j !== i))}
              title="Punkt entfernen"
              aria-label="Punkt entfernen"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-app-text-subtle opacity-0 transition-opacity hover:text-red-600 focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <div className="flex h-8 items-center gap-2.5 border-b border-app-border">
          <Plus className="h-4 w-4 shrink-0 text-app-text-subtle" />
          <input
            value={newCheck}
            onChange={(e) => setNewCheck(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
              e.preventDefault();
              addCheck();
            }}
            placeholder="Punkt hinzufügen"
            aria-label="Checklistenpunkt hinzufügen"
            className="min-w-0 flex-1 bg-transparent text-sm text-app-text outline-none placeholder:text-app-text-subtle"
          />
        </div>
      </div>
    </TaskPanelFrame>
  );
}
